@if (load(); as l) {
<div class="flex flex-col h-full bg-background overflow-hidden">
  <!-- Status & Timing Strip -->
  <div class="px-4 py-3 border-b border-border flex items-center justify-between bg-surface shrink-0">
    <div class="flex items-center gap-2">
      <app-badge variant="secondary" badgeStyle="outline" size="sm">
        {{ l.status | titlecase }}
      </app-badge>
      <app-text variant="muted" size="xs">
        Ready to Invoice
      </app-text>
    </div>
    @if (l.createdAt) {
      <app-text variant="muted" size="xs">
        Created {{ l.createdAt?.toDate() | date:'MMM d, yyyy' }}
      </app-text>
    }
  </div>

  <!-- Scrollable Body -->
  <div class="flex-1 overflow-y-auto p-4 md:p-6 space-y-6">

    <!-- TONU Alert (if applicable) -->
    @if (l.view?.tonuDecision?.status === 'pending') {
    <div
      animate.enter="hv-banner-enter"
      animate.leave="hv-banner-leave"
      class="p-4 rounded-xl border border-warning bg-surface-high flex flex-col gap-3"
    >
      <div class="flex items-start gap-3">
        <div class="p-2 rounded-lg bg-surface border border-warning text-warning shrink-0">
          <lucide-icon name="alert-triangle" class="w-5 h-5"></lucide-icon>
        </div>
        <div class="flex-1">
          <app-heading level="h4" class="text-sm font-semibold text-warning">TONU Decision Pending</app-heading>
          <app-text variant="muted" size="xs" class="mt-0.5 block">
            This load was cancelled after dispatch on {{ l.view?.tonuDecision?.cancelledAt?.toDate() | date:'MMM d, h:mm a' }}.
          </app-text>
          @if (l.view?.tonuDecision?.reason) {
          <div class="mt-2 text-xs p-2 rounded bg-surface border border-border text-foreground italic">
            &ldquo;{{ l.view?.tonuDecision?.reason }}&rdquo;
          </div>
          }
        </div>
      </div>
      <div class="flex justify-end pt-1">
        <app-button variant="primary" size="sm" icon="AlertTriangle" (click)="openTonuDecision()">
          Decide TONU Fee
        </app-button>
      </div>
    </div>
    } @else if (l.view?.tonuDecision?.status === 'eligible') {
    <div
      animate.enter="hv-banner-enter"
      animate.leave="hv-banner-leave"
      class="p-3.5 rounded-xl border border-border bg-surface flex items-center justify-between"
    >
      <div class="flex items-center gap-2.5">
        <div class="w-8 h-8 rounded-lg bg-surface-high border border-border flex items-center justify-center text-primary">
          <lucide-icon name="check-circle-2" class="w-4 h-4"></lucide-icon>
        </div>
        <div>
          <span class="text-xs font-semibold text-foreground block">TONU Approved</span>
          <span class="text-xs text-muted">{{ l.view?.tonuDecision?.decisionNotes || 'Eligible for cancellation fee' }}</span>
        </div>
      </div>
      <app-badge variant="secondary" badgeStyle="outline" size="sm">
        {{ (l.view?.tonuDecision?.amount || 0) | currency }}
      </app-badge>
    </div>
    }

    <!-- Missing Required Billing Documents Alert -->
    @if (!isPendingTonu() && !billingDocValidation().isComplete) {
    <div
      animate.enter="hv-banner-enter"
      animate.leave="hv-banner-leave"
      class="p-4 rounded-xl border border-warning bg-surface-high flex flex-col gap-3"
    >
      <div class="flex items-start gap-3">
        <div class="p-2 rounded-lg bg-surface border border-warning text-warning shrink-0">
          <lucide-icon name="FileWarning" class="w-5 h-5"></lucide-icon>
        </div>
        <div class="flex-1 min-w-0">
          <app-heading level="h4" class="text-sm font-semibold text-warning">Missing Required Billing Documents</app-heading>
          <app-text variant="muted" size="xs" class="mt-0.5 block">
            All required paperwork (Rate Confirmation, PODs for every delivery stop, and any Lumper Receipts) must be attached before this load can be invoiced.
          </app-text>

          <div class="mt-2.5 flex flex-wrap gap-1.5">
            @for (issue of billingDocValidation().missingDocs; track issue.label) {
              <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-surface border border-warning text-warning text-xs font-medium">
                <lucide-icon name="AlertCircle" class="w-3 h-3 shrink-0"></lucide-icon>
                {{ issue.label }}
              </span>
            }
          </div>

          @if (hasMissingLumperReceipt()) {
            <div
              animate.enter="hv-banner-enter"
              animate.leave="hv-banner-leave"
              class="mt-3 p-2.5 rounded-lg bg-surface border border-border"
            >
              <app-checkbox
                size="sm"
                label="Lumper receipt does not exist / paid directly by broker"
                [disabled]="isSavingWaiver()"
                [ngModel]="billingDocValidation().isLumperReceiptWaived"
                (ngModelChange)="toggleLumperReceiptWaived($event)"
              />
            </div>
          }
        </div>
      </div>
      <div class="flex items-center justify-end gap-2 pt-1">
        @if (hasMissingLumperReceipt()) {
          <app-button variant="outline" size="sm" icon="Receipt" (click)="openEditPricing()">
            Attach Lumper Receipt
          </app-button>
        }
        <app-button variant="primary" size="sm" icon="ExternalLink" (click)="viewFullDetails()">
          Upload in Load Details
        </app-button>
      </div>
    </div>
    } @else if (billingDocValidation().hasLumperCharge && billingDocValidation().isLumperReceiptWaived) {
    <div
      animate.enter="hv-banner-enter"
      animate.leave="hv-banner-leave"
      class="p-3 rounded-xl border border-border bg-surface flex items-center justify-between gap-2"
    >
      <app-checkbox
        size="sm"
        label="Lumper receipt waived (paid by broker / N/A)"
        [disabled]="isSavingWaiver()"
        [ngModel]="true"
        (ngModelChange)="toggleLumperReceiptWaived($event)"
      />
    </div>
    }

    <!-- Counterparty Card: Booked From -->
    @if (bookedFrom(); as party) {
    <div class="p-4 rounded-xl border border-border bg-surface space-y-2.5">
      <div class="flex items-center justify-between">
        <span class="text-xs font-semibold uppercase tracking-wider text-muted">Booked From</span>
        <app-badge variant="secondary" badgeStyle="outline" size="sm">
          {{ party.role === 'direct_shipper' ? 'Direct Shipper' : (party.role | titlecase) }}
        </app-badge>
      </div>
      <div class="flex items-center justify-between">
        <app-text size="base" weight="semibold" class="text-foreground">{{ party.name | titlecase }}</app-text>
        <div class="flex items-center gap-2 text-xs text-muted">
          @if (party.dotNumber) { <span>DOT: {{ party.dotNumber }}</span> }
          @if (party.mcNumber) { <span>&bull; MC: {{ party.mcNumber }}</span> }
        </div>
      </div>
    </div>
    }

    <!-- Financial Breakdown Cards -->
    @if (pricingBreakdown(); as pricing) {
    <div class="space-y-3">
      <div class="flex items-center justify-between">
        <span class="text-xs font-semibold uppercase tracking-wider text-muted">Invoice Breakdown</span>
        <app-button variant="ghost" size="sm" icon="Edit" (click)="openEditPricing()">
          Edit
        </app-button>
      </div>

      <div class="p-4 rounded-xl border border-border bg-surface space-y-3 shadow-sm">
        @if (pricing.isTonu || isPendingTonu()) {
          @if (originalBookedRate() > 0) {
          <div class="flex justify-between items-center text-sm py-1 border-b border-border">
            <span class="text-muted">Original Booked Rate (Cancelled)</span>
            <span class="font-medium text-muted line-through">{{ originalBookedRate() | currency }}</span>
          </div>
          }
          @if (isPendingTonu()) {
          <div class="flex justify-between items-center text-sm py-1 border-b border-border">
            <span class="text-muted">Truck Order Not Used (TONU)</span>
            <span class="font-medium text-warning">Pending Decision</span>
          </div>
          } @else {
          <div class="flex justify-between items-center text-sm py-1 border-b border-border">
            <span class="text-muted">Truck Order Not Used (TONU)</span>
            <span class="font-medium text-foreground">{{ pricing.tonu | currency }}</span>
          </div>
          }
        } @else {
        <div class="flex justify-between items-center text-sm py-1 border-b border-border">
          <span class="text-muted">Base Freight Rate</span>
          <span class="font-medium text-foreground">{{ pricing.baseRate | currency }}</span>
        </div>
        }

        @if (pricing.detention > 0 || pricing.detentionHours > 0) {
        <div class="flex justify-between items-center text-sm py-1 border-b border-border">
          <div class="flex items-center gap-1.5">
            <span class="text-muted">Detention Fee</span>
            @if (pricing.detentionHours > 0) {
            <span class="text-xs px-1.5 py-0.5 rounded bg-surface border border-border text-muted">
              {{ pricing.detentionHours }} hrs
            </span>
            }
          </div>
          <span class="font-medium text-foreground">{{ pricing.detention | currency }}</span>
        </div>
        }

        @if (pricing.layover > 0) {
        <div class="flex justify-between items-center text-sm py-1 border-b border-border">
          <span class="text-muted">Layover Fee</span>
          <span class="font-medium text-foreground">{{ pricing.layover | currency }}</span>
        </div>
        }

        @if (pricing.lumper > 0) {
        <div class="flex justify-between items-center text-sm py-1 border-b border-border">
          <span class="text-muted">Lumper Reimbursement</span>
          <span class="font-medium text-foreground">{{ pricing.lumper | currency }}</span>
        </div>
        }

        @if (pricing.fuelSurcharge > 0) {
        <div class="flex justify-between items-center text-sm py-1 border-b border-border">
          <span class="text-muted">Fuel Surcharge</span>
          <span class="font-medium text-foreground">{{ pricing.fuelSurcharge | currency }}</span>
        </div>
        }

        @if (pricing.otherCharges > 0) {
        <div class="flex justify-between items-center text-sm py-1 border-b border-border">
          <span class="text-muted">{{ l.view?.pricing?.otherChargesDescription || 'Other Charges' }}</span>
          <span class="font-medium text-foreground">{{ pricing.otherCharges | currency }}</span>
        </div>
        }

        @if (pricing.deductions > 0) {
        <div class="flex justify-between items-center text-sm py-1 border-b border-border text-danger">
          <span>{{ l.view?.pricing?.deductionsDescription || 'Deductions' }}</span>
          <span class="font-medium">-{{ pricing.deductions | currency }}</span>
        </div>
        }

        <!-- Net Total -->
        <div class="flex justify-between items-center pt-2 text-base">
          <span class="font-semibold text-foreground">Net Invoice Amount</span>
          <span class="font-bold text-lg text-primary">{{ pricing.total | currency }}</span>
        </div>
      </div>
    </div>
    }

    <!-- Stop Dwell & Facility Delay Summary -->
    @if (stopDwellDetails().length > 0) {
    <div class="space-y-3">
      <div class="flex items-center justify-between">
        <span class="text-xs font-semibold uppercase tracking-wider text-muted">Stops &amp; Dwell Times</span>
        @if (totalDetentionHours() > 0) {
        <span class="text-xs font-medium text-warning flex items-center gap-1">
          <lucide-icon name="clock" class="w-3.5 h-3.5"></lucide-icon>
          {{ totalDetentionHours() }} hrs billable detention
        </span>
        }
      </div>

      <div class="space-y-2">
        @for (stop of stopDwellDetails(); track stop.stopNumber) {
        <div class="p-3 rounded-lg border border-border bg-surface flex items-center justify-between text-xs">
          <div class="flex items-center gap-2.5">
            <span class="w-5 h-5 rounded-full bg-surface-high border border-border flex items-center justify-center font-bold text-[10px]">
              {{ stop.stopNumber }}
            </span>
            <div>
              <span class="font-medium text-foreground block">{{ stop.facilityName }}</span>
              <span class="text-muted text-[11px]">{{ stop.cityState }} &bull; {{ stop.stopType | titlecase }}</span>
            </div>
          </div>

          <div class="flex flex-col items-end">
            @if (stop.dwellMinutes !== null) {
            <span class="text-muted">Dwell: {{ stop.dwellMinutes }} min</span>
            @if (stop.isDetentionEligible) {
            <span class="text-warning font-semibold text-[11px] mt-0.5">
              +{{ stop.detentionHours }}h detention
            </span>
            }
            } @else {
            <span class="text-muted">No dwell data</span>
            }
          </div>
        </div>
        }
      </div>
    </div>
    }

    <!-- Document Approval Verification Gate -->
    <div class="space-y-3">
      <div class="flex items-center justify-between">
        <span class="text-xs font-semibold uppercase tracking-wider text-muted">Delivery Proof Documents</span>
        @if (documentVerification().allApproved) {
        <span class="text-xs font-medium text-primary flex items-center gap-1">
          <lucide-icon name="check-circle" class="w-3.5 h-3.5"></lucide-icon>
          All Approved
        </span>
        } @else {
        <span class="text-xs font-medium text-warning flex items-center gap-1">
          <lucide-icon name="alert-circle" class="w-3.5 h-3.5"></lucide-icon>
          {{ documentVerification().pendingDocsCount }} Pending Approval
        </span>
        }
      </div>

      <div class="p-3.5 rounded-xl border border-border bg-surface space-y-2">
        @if (documentVerification().docs.length === 0) {
        <app-text variant="muted" size="xs">No stop-level approval required for this load.</app-text>
        } @else {
        @for (doc of documentVerification().docs; track doc.stopNumber) {
        <div class="flex items-center justify-between text-xs py-1">
          <span class="text-muted">Stop #{{ doc.stopNumber }} {{ doc.type }}</span>
          <app-badge variant="secondary" badgeStyle="outline" size="sm">
            {{ doc.status | titlecase }}
          </app-badge>
        </div>
        }
        }
      </div>
    </div>

  </div>

  <!-- Footer Quick Actions -->
  <div class="p-4 md:p-6 border-t border-border bg-surface flex flex-col gap-2 shrink-0">
    <div class="grid grid-cols-2 gap-2">
      <app-button variant="outline" size="sm" icon="DollarSign" (click)="openEditPricing()">
        Edit Pricing
      </app-button>
      <app-button variant="outline" size="sm" icon="BadgeDollarSign" (click)="openMarkPaid()">
        Mark as Paid
      </app-button>
    </div>
    <div class="flex items-center justify-between pt-1">
      <app-button variant="ghost" size="sm" (click)="markWillNotInvoice()">
        Will Not Invoice
      </app-button>
      <app-button variant="ghost" size="sm" icon="ExternalLink" (click)="viewFullDetails()">
        Load Details &rarr;
      </app-button>
    </div>
  </div>
</div>
} @else {
<div class="flex items-center justify-center h-full p-8">
  <app-loading size="md"></app-loading>
</div>
}
